/* The member sidebar.
 *
 * The client's own list, in their order (docs/CLIENT_TERMINOLOGY.md):
 *
 *   Groups · Pages · Friends · News · Testimonies · Music bands · Singers
 *
 * ADMINISTRATION is its own heading. The client's page gives an approved
 * church admin "the Administration menu", so Members, Permissions, Join
 * questions, Messages, Meetings and Attendance sit together under it, and only
 * for whoever may use them — see adminMenu.
 *
 * GROUPED, not one flat run of seven. Facebook's rail separates what you
 * belong to from what you browse, with a heading and a rule between — seven
 * undifferentiated rows is a list to read rather than a menu to use. The
 * client's order is preserved inside the groups.
 *
 * Items whose screens are not built yet render but are inert. They are in the
 * client's design, so leaving them out would misrepresent the product; the
 * tooltip says why nothing happens rather than a badge on every row.
 */
import { NavLink } from 'react-router-dom'
import { useTabTap } from '@/lib/navigation'
import {
  CalendarCheck,
  Church,
  Contact,
  FileText,
  Mic2,
  Music,
  Newspaper,
  Star,
  Users,
} from 'lucide-react'
import { useMe } from '@/api/queries'
import { useAdminItems } from '@/components/adminMenu'
import { cn } from '@/lib/utils'

type Item = {
  label: string
  icon: typeof Users
  to?: string          // present once the screen exists
  /** Needs a church to mean anything. */
  churchOnly?: boolean
}

type Group = {
  heading?: string
  items: Item[]
}

/* The client's seven, in their order, split where the meaning changes:
   things you are part of, then things you browse. */
const GROUPS: Group[] = [
  {
    items: [
      // The wired-up ones carry a `to`. The rest are the client's menu, shown
      // so the shape of the product reads correctly, and inert.
      { label: 'Groups', icon: Users, to: '/groups' },
      // Every member's: checking in, saying you will be away, and how often
      // you have been. Whoever manages attendance also sets up the services
      // and the check-in location here.
      { label: 'Services', icon: CalendarCheck, to: '/services', churchOnly: true },
      { label: 'Pages', icon: FileText },
      { label: 'Friends', icon: Contact },
    ],
  },
  {
    heading: 'Explore',
    items: [
      { label: 'News', icon: Newspaper },
      { label: 'Testimonies', icon: Star, to: '/testimonies' },
      { label: 'Music bands', icon: Music },
      { label: 'Singers', icon: Mic2 },
    ],
  },
]

export function LeftSidebar() {
  const onNavClick = useTabTap()
  const { data: me } = useMe()
  const inChurch = Boolean(me?.church_id)
  const adminItems = useAdminItems()
  /* Administration goes between what you belong to and what you browse, and
     only when there is something in it. */
  const groups: Group[] = adminItems.length
    ? [GROUPS[0], { heading: 'Administration', items: adminItems }, ...GROUPS.slice(1)]
    : GROUPS

  return (
    <aside className="sticky top-[72px] pr-s2 max-h-[calc(100vh-88px)] overflow-y-auto">
      {/* WHICH CHURCH YOU ARE IN. The rail is on every page, so this is the
          only place the answer is always visible — the name was previously
          only on the Groups page and in the account menu, which meant the feed
          gave no church context at all.

          With no church it becomes the way IN. "Join one" used to be the
          advice on the Groups empty state with nothing to click behind it. */}
      {me ? (
        me.church_name ? (
          <div className="mb-s2 flex items-center gap-s3 rounded-md px-s2 py-s2">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-pill bg-brand-soft text-brand">
              <Church className="h-4 w-4" aria-hidden />
            </span>
            {/* The name alone. "Your church" underneath said nothing the
                name did not, and cost the row the space the name needed —
                "Grace Community Chur…" was truncating with a label under it
                explaining what it was. */}
            <span className="min-w-0 truncate text-md font-semibold">
              {me.church_name}
            </span>
          </div>
        ) : (
          <NavLink
            to="/find-church"
            className="mb-s2 flex items-center gap-s3 rounded-md px-s2 py-s2 transition-colors hover:bg-hover"
          >
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-pill bg-brand-soft text-brand">
              <Church className="h-4 w-4" aria-hidden />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-md font-semibold text-brand">
                Find a church
              </span>
              <span className="block text-sm text-ink-2">You have not joined one</span>
            </span>
          </NavLink>
        )
      ) : null}

      {groups.map((group, i) => (
        <div key={group.heading ?? i}>
          {i > 0 ? <Divider /> : null}
          {group.heading ? (
            <h2 className="px-s2 pb-s1 pt-s2 text-md font-semibold text-ink-2">
              {group.heading}
            </h2>
          ) : null}
          <nav>
            <ul>
              {group.items
                .filter((item) => !item.churchOnly || inChurch)
                .map(({ label, icon: Icon, to }) => (
                <li key={label}>
                  {to ? (
                    <NavLink
                      to={to}
                      onClick={() => onNavClick(to)}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-s3 rounded-md px-s2 py-s2 text-md font-semibold transition-colors',
                          isActive
                            ? 'bg-brand-soft text-brand'
                            : 'text-ink hover:bg-hover-2',
                        )
                      }
                    >
                      <Icon className="h-6 w-6 shrink-0 text-ink-2" aria-hidden />
                      {label}
                    </NavLink>
                  ) : (
                    /* Readable, just quieter than a live link. Fading these to
                       near-invisible made the client's own menu unreadable —
                       "not yet" is not the same as "not worth reading". */
                    <span
                      className="flex cursor-default items-center gap-s3 rounded-md px-s2 py-s2 text-md font-semibold text-ink-2"
                      title={`${label} is not available yet`}
                    >
                      <Icon className="h-6 w-6 shrink-0" aria-hidden />
                      {label}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </div>
      ))}
    </aside>
  )
}

/** Inset from the row padding, so it separates the groups without cutting the
 *  full width of the column. */
function Divider() {
  return <div className="mx-s2 my-s2 h-px bg-line-2" />
}
